<template>
  <div
    class="base-price-range"
    :class="[
      `base-price-range--${props.styleType}`,
      { 'base-price-range-main': props.isMain },
    ]"
  >
    <span v-if="!max" class="no-objs text-black-monochrome font--b5">
      No matches =( <br >
      Check out later.
    </span>
    <base-range-slider
      v-else
      v-model="rangeModel"
      :min="min"
      :max="max"
      :step="0.1"
      :suffix="BASE_CURRENCY.symbol"
      :histogram="bars"
      :compact="props.styleType !== 'base'"
      @change="sendRange"
      @dragging="handleDragging"
    />
  </div>
</template>

<script setup lang="ts">
import eventBus from "~/utilities/composables/eventBus";
import { isEqual } from "lodash";
import { BASE_CURRENCY } from "~/composables/CURRENCIES";

interface EmitValue {
  tokenPrice: {
    gte: number;
    lte: number;
  };
}

interface Emits {
  (event: "emitRange", value: EmitValue): void;
}

interface Props {
  minPrice: number | undefined;
  maxPrice: number | undefined;
  range: { [key: number]: number } | undefined;
  styleType?: "base" | "filters" | "mobile";
  dropFunc?: () => Function;
  updateFunc?: () => Function;
  noDelay?: boolean;
  isMain?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  styleType: "base",
  minPrice: 0,
  maxPrice: 100,
});

const emit = defineEmits<Emits>();
const route = useRoute();

const min = ref(0);
const max = ref(0);
const rangeModel = ref<[number, number]>([0, 1]);
const savedRange = ref<[number, number]>();
const isDragging = ref(false);

const pricesComputed = computed(() =>
  props.range ? Object.keys(props.range).map(Number) : []
);
const labelsComputed = computed(() =>
  props.range ? Object.values(props.range) : []
);

const bars = computed<number[]>(() => {
  const pairs = labelsComputed.value.map((label, index) => ({
    label: Number(label),
    price: Number(pricesComputed.value[index]),
  }));
  if (!pairs.length) return [];
  pairs.sort((a, b) => a.price - b.price);
  const lo = pairs[0].price;
  const hi = pairs[pairs.length - 1].price;
  const span = hi - lo || 1;
  const step = span / 10;
  const buckets = Array.from({ length: 10 }, () => 0);
  pairs.forEach((p) => {
    const idx = Math.min(9, Math.max(0, Math.floor((p.price - lo) / step)));
    buckets[idx] += p.label;
  });
  return buckets;
});

let timeout: ReturnType<typeof setTimeout>;

const sendRange = () => {
  isDragging.value = false;
  if (isEqual(savedRange.value, rangeModel.value)) return;
  timeout = setTimeout(
    () => {
      if (isDragging.value) {
        clearTimeout(timeout);
        return;
      }
      const usdRate = BASE_CURRENCY.value.usdRate;
      emit("emitRange", {
        tokenPrice: {
          gte: parseFloat((rangeModel.value[0] * usdRate).toFixed(1)),
          lte: parseFloat((rangeModel.value[1] * usdRate).toFixed(1)),
        },
      });
      savedRange.value = [rangeModel.value[0], rangeModel.value[1]];
    },
    props.noDelay ? 0 : 1500
  );
};

const handleDragging = () => {
  isDragging.value = true;
};

const dropPriceRange = (numbers: number[]) => {
  nextTick(() => {
    const a = parseFloat((numbers[0] || 0).toFixed(1));
    const b = parseFloat((numbers[1] || 50).toFixed(1));
    rangeModel.value = [a, b];
    min.value = a;
    max.value = b;
  });
};

const gatherCurrencies = () => {
  const baseRate = BASE_CURRENCY.value.rate;
  nextTick(() => {
    let gte =
      typeof route.query[`tokenPrice[gte]`] === "string"
        ? route.query[`tokenPrice[gte]`]
        : props.minPrice;
    gte = parseFloat((parseFloat(gte.toString()) * baseRate).toFixed(1));
    let lte =
      typeof route.query[`tokenPrice[lte]`] === "string"
        ? route.query[`tokenPrice[lte]`]
        : props.maxPrice;
    lte = parseFloat((parseFloat(lte.toString()) * baseRate).toFixed(1));

    min.value = parseFloat(((props.minPrice ?? 0) * baseRate).toFixed(1));
    max.value = parseFloat(((props.maxPrice ?? 1) * baseRate).toFixed(1));
    rangeModel.value = [Number(gte), Number(lte)];
    savedRange.value = [rangeModel.value[0], rangeModel.value[1]];
  });
};

onMounted(() => {
  nextTick(() => {
    gatherCurrencies();
  });
  if (typeof props.dropFunc === "function") {
    const drop = props.dropFunc();
    drop(dropPriceRange);
  }
  eventBus.on("change-currency", gatherCurrencies);
});

onBeforeUnmount(() => {
  eventBus.off("change-currency", gatherCurrencies);
});
</script>

<style scoped lang="scss">
.base-price-range {
  position: relative;
  display: grid;
  width: 100%;
  min-width: 260px;
  max-width: 100%;
  gap: 12px;

  .no-objs {
    text-align: center;
    margin: auto;
    width: 100%;
  }

  &--filters {
    width: fit-content;
    min-width: 189px;
    max-width: 189px;
  }

  &--mobile {
    min-width: 313px;
    max-width: 313px;
  }
}
</style>
